import { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useEscapeToClose } from '../hooks/useEscapeToClose';

type Props = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  // Optional input field - when set, dialog includes a text input and passes its value to onConfirm
  input?: {
    label?: string;
    placeholder?: string;
    required?: boolean;
    multiline?: boolean;
  };
  onConfirm: (inputValue?: string) => void | Promise<void>;
  onClose: () => void;
};

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Potvrdi',
  cancelLabel = 'Otkaži',
  variant = 'danger',
  input,
  onConfirm,
  onClose,
}: Props) {
  useEscapeToClose(onClose);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');

  const handleConfirm = async () => {
    if (input?.required && !inputValue.trim()) {
      setError('Polje je obavezno');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(input ? inputValue : undefined);
      onClose();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={header}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>{title}</h3>
          <button onClick={onClose} disabled={busy} style={closeBtn}><X size={16} /></button>
        </header>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {variant === 'danger' && (
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AlertTriangle size={18} style={{ color: '#fca5a5' }} />
              </div>
            )}
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text)', lineHeight: 1.5 }}>{message}</p>
          </div>

          {input && (
            <div>
              {input.label && (
                <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{input.label}</label>
              )}
              {input.multiline ? (
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={input.placeholder}
                  autoFocus
                  rows={3}
                  style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                />
              ) : (
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                  placeholder={input.placeholder}
                  autoFocus
                  style={inputStyle}
                />
              )}
            </div>
          )}

          {error && (
            <div style={errorBox}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} disabled={busy} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>{cancelLabel}</button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              style={{
                ...(variant === 'danger' ? btnDanger : btnPrimary),
                width: '100%', justifyContent: 'center',
                opacity: busy ? 0.5 : 1,
              }}
            >
              {busy ? '...' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  background: 'var(--color-bg)', color: 'var(--color-text-strong)',
  border: '1px solid var(--color-border)', borderRadius: 6,
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 60,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(0,0,0,0.6)',
};

const dialog: React.CSSProperties = {
  width: '100%', maxWidth: 440,
  background: 'var(--color-bg-surface)',
  borderRadius: 12,
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  border: '1px solid var(--color-border)',
};

const header: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '18px 24px', borderBottom: '1px solid var(--color-border)',
};

const closeBtn: React.CSSProperties = {
  background: 'transparent', border: 'none', color: 'var(--color-text-muted)',
  cursor: 'pointer', padding: 4,
};

const btnPrimary: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '9px 16px', borderRadius: 6, border: 'none',
  background: 'var(--color-primary)', color: '#fff',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '9px 16px', borderRadius: 6, border: 'none',
  background: '#dc2626', color: '#fff',
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 6,
  background: 'var(--color-bg)', color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '10px 12px', borderRadius: 6,
  background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
  fontSize: 13,
};
